import type { InsightState, InvestigationStatus } from '../utils/constants';

type FileType = 'insight' | 'investigation' | 'inquiry';

export type SupportedFileType = 'INVESTIGATION' | 'INQUIRY';

export const SUPPORTED_FILE_TYPES: readonly SupportedFileType[] = ['INVESTIGATION', 'INQUIRY'];

export type TimeUnit = 'DAYS' | 'HOURS' | 'MONTHS' | 'YEARS';
export type RawFile = RawInvestigation | RawInsight | RawInquiry;
export type File = Investigation | Insight | Inquiry;
interface InvestigationOutcome {
  totalEventsFound: number | null;
  totalRawProcessed: number | null;
  eventCategoriesId: number[] | undefined;
  totalFindingsFound: number | null;
}

export interface CreateFilePayload {
  traceName: string;
  dateFrom: string;
  dateTo: string;
  userName?: string;
  systemName?: string;
  ipAddress?: string;
  sendEmailNotification?: boolean;
  assets?: string[];
  reservationId: string;
  type: SupportedFileType;
}

export interface IPDetails {
  status: string;
  rowCount: number | null;
}

export interface Asset {
  assetHostName: string;
  [categoryName: string]: string | Record<string, IPDetails>;
}

interface InvestigationScopeModel {
  ipAddress: string | null;
  systemName: string | null;
  userName: string | null;
}

interface InvestigationData {
  s3Prefix: string;
  id: string;
  dateFrom: string;
  dateTo: string;
  traceName: string;
  status: InvestigationStatus;
  assets: string[];
  runDate: string;
  runBy: string;
  scope: InvestigationScopeModel;
  outcome: InvestigationOutcome;
  isOpen: boolean;
  extractionDetails: Record<string, Asset>;
  numberOfInsights: Record<Lowercase<`${InsightState}`>, number>;
  generatedAt: string | null; //this one does not come from the backend - it's used in the NO_ACTIVITIES_MODAL
}

// normalized fields for AG Grid - these don't come from the backend
// it's just a way of displaying the data easier in the UI since the BE
// is sending the data in a different format/ different nesting level for investigations and insights, but in the UI we want to display them in a similar way
interface NormalizedDataForAgGrid {
  fileDetails: FileDetails;
  status: InvestigationStatus;
  findings: FileFindings;
}

interface BaseFile {
  id: string;
  fileType: FileType;
  createdAt: string;
  createdBy: string;
  isOpen: boolean;
}

export interface Investigation extends NormalizedDataForAgGrid, BaseFile {
  data: InvestigationData;
}

export interface InsightData {
  id: string;
  name: string;
  state: InsightState;
  createdAt: string;
  investigation: string;
  totalFindingsFound: number | null;
  dateFrom: string | null;
  dateTo: string | null;
}

export interface Insight extends NormalizedDataForAgGrid, BaseFile {
  data: InsightData;
}

interface InquiryData {
  criteria: InquiryCriteria;
  status: InvestigationStatus;
  vendor: string;
  inquiryTrace: {
    totalFindingsFound: number | null;
    totalActivitiesFound: number | null;
  };
  createdAt: string;
  dateFrom: string;
  dateTo: string;
  name: string;
  id: string;
}

export interface Inquiry extends NormalizedDataForAgGrid, BaseFile {
  data: InquiryData;
}

interface InquiryCriteria {
  user_name: string;
  ip_address: string;
  system_name: string;
}

export interface FilesResponse {
  openFiles: {
    content: (Investigation | Insight | Inquiry)[];
    totalElements: number;
  };
  closedFiles: {
    content: File[];
    totalElements: number;
  };
}

export interface FilesStatistics {
  openInvestigationsCount: number;
  closedInvestigationsCount: number;
  openInsightsCount: number;
  closedInsightsCount: number;
  openInquiriesCount: number;
  closedInquiriesCount: number;
}

export interface GetFilesStatisticsPayload {
  forCurrentUser: boolean;
  timeAmount: number;
  timeUnit: TimeUnit;
}

interface FileFindings {
  totalFindings: number | null;
  totalEvents?: number | null;
  numberOfInsights?: Record<Lowercase<`${InsightState}`>, number>;
}

interface FileDetails {
  title: string;
  username?: string | null;
  host?: string | null;
  ip?: string | null;
  startDate: string | null;
  endDate?: string | null;
}

export interface RawFilesResponse {
  openFiles: {
    content: RawFile[];
    totalElements: number;
  };
  closedFiles: {
    content: RawFile[];
    totalElements: number;
  };
}

interface RawInvestigation extends Omit<BaseFile, 'fileType'> {
  fileType: 'investigation';
  data: InvestigationData;
}

interface RawInsight extends Omit<RawInvestigation, 'data' | 'fileType'> {
  fileType: 'insight';
  data: InsightData;
}

interface RawInquiry extends Omit<RawInvestigation, 'data' | 'fileType'> {
  fileType: 'inquiry';
  data: InquiryData;
}

export interface FileExportStartPayload {
  fileName?: string;
}
